import React, { useState, useEffect } from 'react';
import '../styles/FacturasBuscador.css';
import '../styles/FacturasEditor.css';
import { FacturaData, FacturaForm } from './FacturaForm';

export interface FacturasBuscadorProps {
  cuit?: string;
  busqueda?: string;
  ocultarBusqueda?: boolean;
  apiBaseUrl?: string;
  enabled?: boolean;
  onFacturaUpdated?: (facturaId: string, data: FacturaData) => void;
  onFacturaDeleted?: (facturaId: string) => void;
  onError?: (error: string) => void;
}

interface FacturaItem {
  tipo: string;
  paginas: number;
  caracteres: number | null;
  campos: {
    afip: {
      cae: string;
      cae_vencimiento: string;
    };
    items: Array<{
      cantidad: number;
      concepto: string;
      subtotal: number;
      precio_unit: number;
    }>;
    emisor: {
      cuit: string;
      razon_social: string;
      condicion_iva: string;
    };
    importes: {
      neto: number;
      total: number;
      iva_21: number | null;
      iva_105: number | null;
      otros_impuestos: number | null;
    };
    receptor: {
      cuit: string;
    };
    comprobante: {
      tipo: string;
      fecha: string;
      letra: string;
      numero: string;
      punto_venta: string;
    };
  };
  extraction_failed: boolean;
  doc_id: string;
  dataset_path: string;
  storage: string;
  pdf_url: string;
}

export const FacturasBuscador: React.FC<FacturasBuscadorProps> = ({
  cuit: cuitProp,
  busqueda: busquedaProp,
  ocultarBusqueda,
  apiBaseUrl = 'https://doc-ia-processor.sindicalia.com.ar',
  enabled = false,
  onFacturaUpdated,
  onFacturaDeleted,
  onError
}) => {
  const [cuit, setCuit] = useState(cuitProp || '');
  const [facturas, setFacturas] = useState<FacturaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [selectedFactura, setSelectedFactura] = useState<string | null>(null);
  const [selectedDatasetPath, setSelectedDatasetPath] = useState<string | null>(null);
  const [editingFactura, setEditingFactura] = useState<FacturaData | null>(null);
  const [currentPdfUrl, setCurrentPdfUrl] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | '' }>({ text: '', type: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [facturaToDelete, setFacturaToDelete] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const limit = 5;

  // Si se recibe cuit como prop, hacer búsqueda automática
  useEffect(() => {
    if (cuitProp) {
      setCuit(() => cuitProp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuitProp]);

    useEffect(() => {
    if (cuit) {
      handleSearch(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuit]);


  const handleSearch = async (page: number = 0) => {
    const cuitNormalizado = cuit.replace(/[-\s]/g, '');
    setFacturas([]);
    setTotalResults(0);

    if (!cuitNormalizado) {
      setMessage({ text: 'Ingresá un CUIT para buscar', type: 'error' });
      return;
    }

    if (!/^\d{11}$/.test(cuitNormalizado)) {
      setMessage({ text: 'El CUIT debe tener 11 dígitos', type: 'error' });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });
    setHasSearched(true);

    try {
      const response = await fetch(
        `${apiBaseUrl}/facturas?cuit=${cuitNormalizado}&limit=${limit}&offset=${page * limit}`
      );

      if (!response.ok) {
        throw new Error('Error al buscar facturas');
      }

      const data = await response.json();
      setFacturas(data.facturas || []);
      setTotalResults(data.total || 0);
      setCurrentPage(page);

      if ((data.facturas || []).length === 0) {
        setMessage({ text: 'No se encontraron facturas para este CUIT', type: 'error' });
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Error al buscar facturas';
      setMessage({ text: errorMsg, type: 'error' });
      if (onError) onError(errorMsg);
    } finally {
      setLoading(false);
    }
  };



  const handleEdit = async (docId: string) => {
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await fetch(`${apiBaseUrl}/facturas/${encodeURIComponent(docId)}`);

      if (!response.ok) {
        throw new Error('Error al cargar la factura');
      }

      const data = await response.json();
      console.log('Datos recibidos de la API:', data);

      // Guardar el dataset_path para usarlo al guardar
      if (data.dataset_path) {
        setSelectedDatasetPath(data.dataset_path);
      }

      // Extraer datos de gold o pred - estos vienen en data.campos
      const rawData = data.campos || data.gold || data.pred || {};
      console.log('Raw data extraído:', rawData);

      // Transformar al formato FacturaData
      const facturaData: FacturaData = {
        comprobante: rawData.comprobante || {
          tipo: null,
          letra: null,
          punto_venta: null,
          numero: null,
          fecha: null
        },
        emisor: rawData.emisor || {
          cuit: null,
          razon_social: null,
          condicion_iva: null
        },
        receptor: rawData.receptor || {
          cuit: null
        },
        importes: rawData.importes || {
          neto: null,
          iva_21: null,
          iva_105: null,
          otros_impuestos: null,
          total: null
        },
        afip: rawData.afip || {
          cae: null,
          cae_vencimiento: null
        },
        items: rawData.items || []
      };

      setEditingFactura(facturaData);
      // Usar doc_id que viene en la respuesta
      setSelectedFactura(data.doc_id || docId);

      // Guardar URL del PDF si existe
      if (data.pdf_url) {
        setCurrentPdfUrl(data.pdf_url);
      } else {
        setCurrentPdfUrl(null);
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Error al cargar la factura';
      setMessage({ text: errorMsg, type: 'error' });
      if (onError) onError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Si se recibe busqueda como prop, cargarla automáticamente
  useEffect(() => {
    if (busquedaProp) {
      handleEdit(busquedaProp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busquedaProp]);

  const handleDeleteClick = (facturaId: string) => {
    setFacturaToDelete(facturaId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!facturaToDelete) return;

    setShowDeleteConfirm(false);
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await fetch(`${apiBaseUrl}/facturas/${encodeURIComponent(facturaToDelete)}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Error al eliminar la factura');
      }

      setMessage({ text: '✓ Factura eliminada correctamente', type: 'success' });
      if (onFacturaDeleted) onFacturaDeleted(facturaToDelete);

      // Recargar la búsqueda
      setTimeout(() => {
        handleSearch(currentPage);
      }, 1500);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Error al eliminar la factura';
      setMessage({ text: errorMsg, type: 'error' });
      if (onError) onError(errorMsg);
    } finally {
      setLoading(false);
      setFacturaToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setFacturaToDelete(null);
  };

  const validateFormData = (data: FacturaData): string[] => {
    const errors: string[] = [];

    if (!data.comprobante.tipo || data.comprobante.tipo.trim() === '') {
      errors.push('El campo "Tipo" de comprobante es obligatorio');
    }
    if (!data.comprobante.letra || data.comprobante.letra.trim() === '') {
      errors.push('El campo "Letra" de comprobante es obligatorio');
    }
    if (!data.comprobante.punto_venta || data.comprobante.punto_venta.trim() === '') {
      errors.push('El campo "Punto venta" es obligatorio');
    }
    if (!data.comprobante.numero || data.comprobante.numero.trim() === '') {
      errors.push('El campo "Número" de comprobante es obligatorio');
    }
    if (!data.comprobante.fecha || data.comprobante.fecha.trim() === '') {
      errors.push('El campo "Fecha" de comprobante es obligatorio');
    }

    if (!data.emisor.cuit || data.emisor.cuit.trim() === '') {
      errors.push('El campo "CUIT" del emisor es obligatorio');
    } else if (!/^\d{11}$/.test(data.emisor.cuit)) {
      errors.push('El CUIT del emisor debe tener exactamente 11 dígitos');
    }
    if (!data.emisor.condicion_iva || data.emisor.condicion_iva.trim() === '') {
      errors.push('El campo "Condición IVA" del emisor es obligatorio');
    }
    if (!data.emisor.razon_social || data.emisor.razon_social.trim() === '') {
      errors.push('El campo "Razón Social" del emisor es obligatorio');
    }

    if (!data.afip.cae || data.afip.cae.trim() === '') {
      errors.push('El campo "CAE" es obligatorio');
    }
    if (!data.afip.cae_vencimiento || data.afip.cae_vencimiento.trim() === '') {
      errors.push('El campo "Vencimiento CAE" es obligatorio');
    }

    if (!data.items || data.items.length === 0) {
      errors.push('Debe agregar al menos un item/concepto');
    } else {
      const validItems = data.items.filter(item =>
        item.concepto && item.concepto.trim() !== '' && item.subtotal !== null
      );

      if (validItems.length === 0) {
        errors.push('Debe haber al menos un item con "Concepto" y "Subtotal" completos');
      }

      for (let i = 0; i < data.items.length; i++) {
        const item = data.items[i];
        if (item.concepto && item.concepto.trim() !== '') {
          if (item.subtotal === null || isNaN(item.subtotal)) {
            errors.push(`El item ${i + 1} tiene concepto pero falta el subtotal o no es un número válido`);
          }
        }
      }
    }

    if (data.importes.neto === null || isNaN(data.importes.neto)) {
      errors.push('El campo "Neto" en importes es obligatorio y debe ser un número válido');
    }
    if (data.importes.total === null || isNaN(data.importes.total)) {
      errors.push('El campo "Total" en importes es obligatorio y debe ser un número válido');
    }

    return errors;
  };

  const handleSave = async (data: FacturaData) => {
    if (!selectedDatasetPath) {
      setMessage({ text: 'Error: No se encontró el dataset_path', type: 'error' });
      return;
    }

    const errors = validateFormData(data);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await fetch(`${apiBaseUrl}/facturas/${encodeURIComponent(selectedDatasetPath)}/gold`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error('Error al guardar los cambios');
      }

      setMessage({ text: '✓ Factura actualizada correctamente', type: 'success' });
      if (onFacturaUpdated && selectedFactura) onFacturaUpdated(selectedFactura, data);

      setTimeout(() => {
        setEditingFactura(null);
        setSelectedFactura(null);
        setSelectedDatasetPath(null);
        handleSearch(currentPage);
      }, 1500);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Error al guardar los cambios';
      setMessage({ text: errorMsg, type: 'error' });
      if (onError) onError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditingFactura(null);
    setSelectedFactura(null);
    setSelectedDatasetPath(null);
    setCurrentPdfUrl(null);
    setValidationErrors([]);
  };

  const totalPages = Math.ceil(totalResults / limit);

  // Utilidades para URLs
  const isPresignedUrl = (url: string | null): boolean => {
    if (!url) return false;
    return (
      url.includes('X-Amz-Algorithm') ||
      url.includes('AWSAccessKeyId') ||
      url.includes('Signature')
    );
  };

  const addCacheBusting = (url: string): string => {
    if (!url || isPresignedUrl(url)) {
      return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}t=${Date.now()}`;
  };

  // Renderizar vista previa del PDF
  const renderPdfPreview = () => {
    if (!selectedFactura) return null;

    const baseUrl = `${apiBaseUrl}/facturas/${encodeURIComponent(selectedFactura)}/file`;

    if (currentPdfUrl) {
      return (
        <iframe
          src={currentPdfUrl}
          style={{ width: '100%', height: '100%', border: 'none' }}
          title="Factura PDF"
        />
      );
    }

    // Fallback a PDF original
    return (
      <iframe
        src={addCacheBusting(`${baseUrl}/original.pdf`)}
        style={{ width: '100%', height: '100%', border: 'none' }}
        title="Factura PDF"
      />
    );
  };

  return (
    <div className="facturas-buscador">
      {/* Validation Errors Popup */}
      {validationErrors.length > 0 && (
        <div className="validation-overlay" onClick={() => setValidationErrors([])}>
          <div className="validation-popup" onClick={(e) => e.stopPropagation()}>
            <div className="validation-header">
              <h3>⚠️ Errores de Validación</h3>
              <button className="validation-close" onClick={() => setValidationErrors([])}>✕</button>
            </div>
            <div className="validation-content">
              <p className="validation-intro">Por favor, corregí los siguientes errores antes de guardar:</p>
              <ul className="validation-list">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
            <div className="validation-footer">
              <button className="validation-ok-button" onClick={() => setValidationErrors([])}>
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {showDeleteConfirm && (
        <div className="confirm-overlay" onClick={cancelDelete}>
          <div className="confirm-popup" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-header">
              <h3>⚠️ Eliminar Factura</h3>
              <button className="confirm-close" onClick={cancelDelete}>✕</button>
            </div>
            <div className="confirm-content">
              <div className="confirm-icon">🗑️</div>
              <p className="confirm-message">¿Estás seguro de que querés eliminar esta factura?</p>
              <p className="confirm-hint">Esta acción no se puede deshacer.</p>
            </div>
            <div className="confirm-footer">
              <button className="confirm-cancel-button" onClick={cancelDelete}>
                Cancelar
              </button>
              <button className="confirm-ok-button confirm-delete-button" onClick={confirmDelete}>
                Sí, eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="buscador-container">
        {!editingFactura ? (
          <>
            {/* Search Form - Oculto si se recibe cuit como prop o si ocultarBusqueda es true */}
            {!cuitProp && !ocultarBusqueda && (
              <div className="search-section">
                <h2>🔍 Buscar Facturas</h2>
                <p className="search-subtitle">Ingresá el CUIT para buscar las facturas asociadas</p>

                <div className="search-form">
                  <div className="search-input-group">
                    <label htmlFor="cuit-search">CUIT:</label>
                    <input
                      id="cuit-search"
                      type="text"
                      value={cuit}
                      onChange={(e) => setCuit(e.target.value)}
                      placeholder="20123456789"
                      maxLength={11}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSearch(0);
                        }
                      }}
                    />
                    <button
                      onClick={() => handleSearch(0)}
                      disabled={loading}
                      className="btn-search"
                    >
                      {loading ? 'Buscando...' : 'Buscar'}
                    </button>
                  </div>
                </div>

                {message.text && (
                  <div className={`search-message ${message.type}`}>
                    {message.text}
                  </div>
                )}
              </div>
            )}

            {/* Results List */}
            {facturas.length > 0 ? (
              <div className="results-section">
                <h3>Resultados ({totalResults})</h3>

                <div className="facturas-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Comprobante</th>
                        <th>Fecha</th>
                        <th>Emisor</th>
                        <th>CUIT</th>
                        <th>Total</th>
                        <th style={{ textAlign: 'center' }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {facturas.map((factura) => (
                        <tr key={factura.doc_id}>
                          <td>{factura.campos.comprobante.tipo}</td>
                          <td>{factura.campos.comprobante.letra} {factura.campos.comprobante.punto_venta}-{factura.campos.comprobante.numero}</td>
                          <td>{factura.campos.comprobante.fecha}</td>
                          <td>{factura.campos.emisor.razon_social}</td>
                          <td>{factura.campos.emisor.cuit}</td>
                          <td>${factura.campos.importes.total.toFixed(2)}</td>
                          <td className="actions-cell" style={{ textAlign: 'center' }}>
                            <button
                              onClick={() => handleEdit(factura.doc_id)}
                              className="btn-action btn-edit"
                              title={enabled ? "Ver" : "Editar"}
                              disabled={loading}
                            >
                              {enabled ? "🔍" : "✏️"}
                            </button>
                            {!enabled && (
                              <button
                                onClick={() => handleDeleteClick(factura.doc_id)}
                                className="btn-action btn-delete"
                                title="Eliminar"
                                disabled={loading}
                              >
                                🗑️
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      onClick={() => handleSearch(currentPage - 1)}
                      disabled={currentPage === 0 || loading}
                      className="btn-page"
                    >
                      ← Anterior
                    </button>
                    <span className="page-info">
                      Página {currentPage + 1} de {totalPages}
                    </span>
                    <button
                      onClick={() => handleSearch(currentPage + 1)}
                      disabled={currentPage >= totalPages - 1 || loading}
                      className="btn-page"
                    >
                      Siguiente →
                    </button>
                  </div>
                )}
              </div>
            ) : (
              hasSearched && !loading && (
                <div className="no-results-message" style={{ 
                  textAlign: 'center', 
                  padding: '3rem', 
                  color: '#666', 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: '8px', 
                  border: '1px dashed #dee2e6', 
                  marginTop: '1rem' 
                }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '1rem', opacity: 0.5 }}>🔍</div>
                  <p style={{ fontSize: '1.1rem', fontWeight: 500, margin: '0 0 0.5rem 0' }}>No se encontraron facturas cargadas anteriormente</p>
                  {!ocultarBusqueda && (
                    <p style={{ fontSize: '0.9rem', color: '#888', margin: 0 }}>Verificá el CUIT ingresado e intentá nuevamente</p>
                  )}
                </div>
              )
            )}
          </>
        ) : (
          <>
            {/* Edit Form */}
            <div className="edit-section">
              <div className="edit-header">
                <h2>{enabled ? "👁️ Ver Factura" : "✏️ Editar Factura"}</h2>
                {!ocultarBusqueda && (
                  <button onClick={handleCancel} className="btn-back">
                    ← Volver a resultados
                  </button>
                )}
              </div>

              {message.text && (
                <div className={`edit-message ${message.type}`}>
                  {message.text}
                </div>
              )}

              <div className="editor-layout">
                {/* Panel izquierdo: PDF */}
                <div className="image-panel">
                  <div className="pdf-container">
                    <div className="pdf-header">
                      <h3>📄 Factura</h3>
                    </div>
                    {renderPdfPreview()}
                  </div>
                </div>

                {/* Panel derecho: Formulario */}
                <div className="form-panel">
                  <div className="form-panel-header">
                    <div>
                      <h3>{enabled ? "📋 Datos de la Factura" : "✓ Verificá los Datos"}</h3>
                      <p className="form-panel-subtitle">
                        {enabled ? "Información de la factura" : "Revisá que la información sea correcta antes de guardar"}
                      </p>
                    </div>
                  </div>

                  <FacturaForm
                    initialData={editingFactura}
                    validationErrors={validationErrors}
                    onSubmit={handleSave}
                    onCancel={handleCancel}
                    showCancelButton={true}
                    facturaId={selectedFactura!}
                    readOnly={enabled}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FacturasBuscador;
